import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LuFileText, LuSearchX, LuSettings2 } from 'react-icons/lu';
import { API_BASE_URL } from '../../config';
import { sourceLabel, sourceTone } from '../../utils/sources.js';
import { Card, CardHeader } from '../ui/Card';
import { Table, THead, Th, SortTh, TBody, Tr, Td } from '../ui/Table';
import { SearchInput } from '../ui/SearchInput';
import Alert from '../ui/Alert';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import Skeleton from '../ui/Skeleton';
import ConfigDetailModal from './ConfigDetailModal';

/** Client configurations (super admin): list, search, sort, and the edition modal. */
const ConfigManagement = ({ fetchWithAuth }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchConfigs = async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/configs`);
        if (!response.ok) throw new Error('Failed to fetch configurations');
        setConfigs(await response.json());
        setError(null);
      } catch (err) {
        console.error('Error fetching configurations:', err);
        setError('Impossible de charger les configurations. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };
    fetchConfigs();
  }, [fetchWithAuth]);

  const handleOpenConfig = useCallback(async (configId) => {
    try {
      setOpening(configId);
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/configs/${configId}`);
      if (!response.ok) throw new Error('Failed to fetch configuration details');
      setSelectedConfig(await response.json());
    } catch (err) {
      console.error('Error fetching configuration details:', err);
      setError('Impossible de charger le détail de la configuration. Veuillez réessayer.');
    } finally {
      setOpening(null);
    }
  }, [fetchWithAuth]);

  // ?configId=… (link from a report) opens the configuration directly.
  useEffect(() => {
    const configId = searchParams.get('configId');
    if (!configId || configs.length === 0) return;
    if (configs.some((config) => config.id.toString() === configId)) {
      handleOpenConfig(parseInt(configId, 10));
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('configId');
        return next;
      });
    }
  }, [searchParams, configs, setSearchParams, handleOpenConfig]);

  const handleSave = async (configId, data) => {
    try {
      setSaving(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/configs/${configId}/weekly`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update configuration');
      }
      setSelectedConfig((prev) => ({ ...prev, ...data }));
      setConfigs((prev) => prev.map((config) => (config.id === configId ? { ...config, ...data } : config)));
      return { success: true };
    } catch (err) {
      console.error('Error updating configuration:', err);
      return { success: false, error: err.message };
    } finally {
      setSaving(false);
    }
  };

  const requestSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };

  const rows = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const filtered = q
      ? configs.filter((c) => c.name.toLowerCase().includes(q) || c.source_type.toLowerCase().includes(q) || (c.slug || '').toLowerCase().includes(q))
      : configs;
    if (!sortConfig.key) return filtered;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = a[sortConfig.key];
      const vb = b[sortConfig.key];
      if (va === vb) return 0;
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      return (va < vb ? -1 : 1) * dir;
    });
  }, [configs, searchQuery, sortConfig]);

  return (
    <Card>
      <CardHeader
        icon={LuSettings2}
        title="Configurations"
        badge={!loading && <Badge tone="violet" size="sm">{configs.length}</Badge>}
        subtitle="Clients, sources de données, partage public et analyses"
        actions={<SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Nom, slug ou source…" className="w-full sm:w-64" />}
      />

      {error && <div className="px-4 pt-4 sm:px-5"><Alert tone="danger">{error}</Alert></div>}

      {loading ? (
        <div className="space-y-2 p-4 sm:p-5">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12" />)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={LuSearchX} tone="gray" title="Aucune configuration trouvée" description="Essayez une autre recherche." />
      ) : (
        <Table>
          <THead>
            <SortTh column="name" sort={sortConfig} onSort={requestSort}>Client</SortTh>
            <SortTh column="source_type" sort={sortConfig} onSort={requestSort}>Source</SortTh>
            <SortTh column="public_sharing_enabled" sort={sortConfig} onSort={requestSort}>Partage public</SortTh>
            <SortTh column="is_source_active" sort={sortConfig} onSort={requestSort}>Collecte</SortTh>
            <SortTh column="deleted_at" sort={sortConfig} onSort={requestSort}>Statut</SortTh>
            <Th className="text-right">Actions</Th>
          </THead>
          <TBody>
            {rows.map((config) => (
              <Tr key={config.id} className={config.deleted_at ? 'opacity-60' : ''}>
                <Td className="whitespace-nowrap">
                  <span className="flex items-center gap-2.5">
                    <Avatar text={config.name} size="sm" />
                    <span className="min-w-0">
                      <span className="block font-medium text-gray-900 dark:text-white">{config.name}</span>
                      <span className="block font-mono text-xs text-gray-400 dark:text-gray-500">{config.slug}</span>
                    </span>
                  </span>
                </Td>
                <Td><Badge tone={sourceTone(config.source_type)} size="sm">{sourceLabel(config.source_type)}</Badge></Td>
                <Td>
                  {config.public_sharing_enabled
                    ? <Badge tone="green" size="sm" dot>Activé</Badge>
                    : <Badge tone="gray" size="sm">Désactivé</Badge>}
                </Td>
                <Td>
                  {config.is_source_active !== false
                    ? <Badge tone="green" size="sm" dot>Active</Badge>
                    : <Badge tone="amber" size="sm" dot title="Le cron hebdomadaire ne récupère plus ce client">Inactive</Badge>}
                </Td>
                <Td>
                  {config.deleted_at
                    ? <Badge tone="red" size="sm">Supprimé</Badge>
                    : <Badge tone="gray" size="sm">En service</Badge>}
                </Td>
                <Td className="whitespace-nowrap text-right">
                  <span className="inline-flex items-center gap-1.5">
                    <Button as={Link} to={`/w/${config.slug}`} target="_blank" rel="noopener noreferrer" size="sm" variant="ghost" icon={LuFileText} title="Voir les rapports (nouvel onglet)">Rapports</Button>
                    <Button size="sm" icon={LuSettings2} onClick={() => handleOpenConfig(config.id)} loading={opening === config.id}>Modifier</Button>
                  </span>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {selectedConfig && (
        <ConfigDetailModal config={selectedConfig} onClose={() => setSelectedConfig(null)} onSave={handleSave} isLoading={saving} fetchWithAuth={fetchWithAuth} />
      )}
    </Card>
  );
};

export default ConfigManagement;
